import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Copy, CreditCard, Instagram, LogIn, Mail, Puzzle, Search, ShieldCheck, Trash2 } from 'lucide-react';
import AdminSection from '../components/AdminSection';
import {
  fetchPaymentSettings, fetchPlugins, savePaymentSettings,
  fetchEmailSettings, saveEmailSettings, sendTestEmail,
  fetchSocialSettings, saveSocialSettings,
  fetchSeoSettings, saveSeoSettings,
  fetchSocialFeedAdmin, addSocialFeedItem, deleteSocialFeedItem,
} from '../lib/adminApi';

const inputCls =
  'w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-500 outline-none transition focus:border-[#1fa5a3] focus:ring-4 focus:ring-[#1fa5a3]/10';

const TABS = [
  { id: 'payments', label: 'Pagos', icon: CreditCard },
  { id: 'email', label: 'Correo', icon: Mail },
  { id: 'social', label: 'Acceso social', icon: LogIn },
  { id: 'social-feed', label: 'Redes sociales', icon: Instagram },
  { id: 'seo', label: 'SEO', icon: Search },
];

function CopyField({ label, value, hint }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</p>
      <div className="mt-1.5 flex items-center gap-2">
        <code className="flex-1 truncate rounded-xl bg-slate-100 px-3 py-2 text-xs text-slate-700">{value}</code>
        <button
          type="button"
          onClick={async () => {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl border border-slate-200 text-slate-600 transition hover:border-[#1fa5a3] hover:text-[#1fa5a3]"
        >
          {copied ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>
      {hint ? <p className="mt-1 text-xs text-slate-500">{hint}</p> : null}
    </div>
  );
}

export default function AdminSettings() {
  const queryClient = useQueryClient();
  const [msg, setMsg] = useState({ ok: '', err: '' });
  const [tab, setTab] = useState('payments');
  const [form, setForm] = useState({
    enabled: false,
    merchant_code: '',
    api_base_url: 'https://api.sumup.com',
    api_key: '',
    webhook_secret: '',
  });

  const settingsQuery = useQuery({ queryKey: ['admin', 'payments'], queryFn: fetchPaymentSettings });
  const pluginsQuery = useQuery({ queryKey: ['admin', 'plugins'], queryFn: fetchPlugins });
  const settings = settingsQuery.data;

  // Secrets are never returned by the API, so their inputs always start empty:
  // leaving them blank keeps whatever is already stored on the server.
  useEffect(() => {
    if (!settings) return;
    setForm((current) => ({
      ...current,
      enabled: Boolean(settings.enabled),
      merchant_code: settings.merchant_code || '',
      api_base_url: settings.api_base_url || 'https://api.sumup.com',
    }));
  }, [settings]);

  const saveMutation = useMutation({
    mutationFn: () => savePaymentSettings(form),
    onSuccess: async () => {
      setMsg({ ok: 'Ajustes de pago guardados.', err: '' });
      setForm((c) => ({ ...c, api_key: '', webhook_secret: '' }));
      await queryClient.invalidateQueries({ queryKey: ['admin', 'payments'] });
      await queryClient.invalidateQueries({ queryKey: ['admin', 'plugins'] });
    },
    onError: (e) => setMsg({ ok: '', err: e.message || 'No se pudieron guardar los ajustes.' }),
  });

  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));
  const plugins = pluginsQuery.data?.items || [];

  // --- Email (SMTP) settings ---
  const [emailForm, setEmailForm] = useState({
    host: '', port: 587, username: '', password: '',
    from_email: '', from_name: 'OWA Wild Adventure', security: 'starttls', notify_email: '',
  });
  const [emailMsg, setEmailMsg] = useState({ ok: '', err: '' });
  const emailQuery = useQuery({ queryKey: ['admin', 'email'], queryFn: fetchEmailSettings });
  const email = emailQuery.data;
  useEffect(() => {
    if (!email) return;
    setEmailForm((c) => ({
      ...c,
      host: email.host || '', port: email.port || 587, username: email.username || '',
      from_email: email.from_email || '', from_name: email.from_name || 'OWA Wild Adventure',
      security: email.security || 'starttls', notify_email: email.notify_email || '',
    }));
  }, [email]);
  const setE = (k, v) => setEmailForm((c) => ({ ...c, [k]: v }));
  const emailSave = useMutation({
    mutationFn: () => saveEmailSettings(emailForm),
    onSuccess: async () => {
      setEmailMsg({ ok: 'Ajustes de correo guardados.', err: '' });
      setEmailForm((c) => ({ ...c, password: '' }));
      await queryClient.invalidateQueries({ queryKey: ['admin', 'email'] });
    },
    onError: (e) => setEmailMsg({ ok: '', err: e.message || 'No se pudo guardar.' }),
  });
  const emailTest = useMutation({
    mutationFn: () => sendTestEmail(emailForm.notify_email || email?.notify_email),
    onSuccess: (r) => setEmailMsg({ ok: `Email de prueba enviado a ${r.to}.`, err: '' }),
    onError: (e) => setEmailMsg({ ok: '', err: e.message || 'No se pudo enviar la prueba.' }),
  });

  // --- Social login settings ---
  const [socialForm, setSocialForm] = useState({ google_client_id: '', facebook_app_id: '', facebook_app_secret: '' });
  const [socialMsg, setSocialMsg] = useState({ ok: '', err: '' });
  const socialQuery = useQuery({ queryKey: ['admin', 'social'], queryFn: fetchSocialSettings });
  const social = socialQuery.data;
  useEffect(() => {
    if (!social) return;
    setSocialForm((c) => ({ ...c, google_client_id: social.google_client_id || '', facebook_app_id: social.facebook_app_id || '' }));
  }, [social]);
  const setS = (k, v) => setSocialForm((c) => ({ ...c, [k]: v }));
  const socialSave = useMutation({
    mutationFn: () => saveSocialSettings(socialForm),
    onSuccess: async () => {
      setSocialMsg({ ok: 'Acceso social guardado.', err: '' });
      setSocialForm((c) => ({ ...c, facebook_app_secret: '' }));
      await queryClient.invalidateQueries({ queryKey: ['admin', 'social'] });
    },
    onError: (e) => setSocialMsg({ ok: '', err: e.message || 'No se pudo guardar.' }),
  });

  // --- SEO (global defaults, GA4, Search Console, sitemap toggle) ---
  const [seoForm, setSeoForm] = useState({
    default_meta_title: '', default_meta_description: '',
    ga4_measurement_id: '', search_console_verification: '', sitemap_enabled: true,
  });
  const [seoMsg, setSeoMsg] = useState({ ok: '', err: '' });
  const seoQuery = useQuery({ queryKey: ['admin', 'seo'], queryFn: fetchSeoSettings });
  const seo = seoQuery.data;
  useEffect(() => {
    if (!seo) return;
    setSeoForm({
      default_meta_title: seo.default_meta_title || '',
      default_meta_description: seo.default_meta_description || '',
      ga4_measurement_id: seo.ga4_measurement_id || '',
      search_console_verification: seo.search_console_verification || '',
      sitemap_enabled: seo.sitemap_enabled !== false,
    });
  }, [seo]);
  const setSeo = (k, v) => setSeoForm((c) => ({ ...c, [k]: v }));
  const seoSave = useMutation({
    mutationFn: () => saveSeoSettings(seoForm),
    onSuccess: async () => {
      setSeoMsg({ ok: 'Ajustes SEO guardados.', err: '' });
      await queryClient.invalidateQueries({ queryKey: ['admin', 'seo'] });
    },
    onError: (e) => setSeoMsg({ ok: '', err: e.message || 'No se pudieron guardar los ajustes SEO.' }),
  });

  // --- Social feed (curated Instagram / TikTok posts) ---
  const [feedForm, setFeedForm] = useState({ platform: 'instagram', url: '', caption: '' });
  const [feedMsg, setFeedMsg] = useState({ ok: '', err: '' });
  const feedQuery = useQuery({ queryKey: ['admin', 'social-feed'], queryFn: fetchSocialFeedAdmin });
  const feedItems = feedQuery.data?.items || [];
  const feedAdd = useMutation({
    mutationFn: () => addSocialFeedItem(feedForm),
    onSuccess: async () => {
      setFeedMsg({ ok: 'Publicación añadida.', err: '' });
      setFeedForm((c) => ({ ...c, url: '', caption: '' }));
      await queryClient.invalidateQueries({ queryKey: ['admin', 'social-feed'] });
      await queryClient.invalidateQueries({ queryKey: ['social-feed'] });
    },
    onError: (e) => setFeedMsg({ ok: '', err: e.message || 'No se pudo añadir el enlace.' }),
  });
  const feedDelete = useMutation({
    mutationFn: (id) => deleteSocialFeedItem(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'social-feed'] });
      await queryClient.invalidateQueries({ queryKey: ['social-feed'] });
    },
    onError: (e) => setFeedMsg({ ok: '', err: e.message || 'No se pudo eliminar.' }),
  });

  return (
    <AdminSection
      eyebrow="Admin"
      title="Ajustes"
      description="Configuración de los módulos conectados a la web. Las claves se guardan en el servidor y nunca se vuelven a mostrar."
    >
      <div className="mb-6 flex flex-wrap gap-1 border-b border-slate-200">
        {TABS.map((tb) => (
          <button
            key={tb.id}
            type="button"
            onClick={() => setTab(tb.id)}
            className={`-mb-px flex items-center gap-2 rounded-t-xl border-b-2 px-4 py-2.5 text-sm font-semibold transition ${
              tab === tb.id
                ? 'border-[#1fa5a3] text-[#1fa5a3]'
                : 'border-transparent text-slate-500 hover:text-[#14213d]'
            }`}
          >
            <tb.icon className="h-4 w-4" />{tb.label}
          </button>
        ))}
      </div>

      {tab === 'payments' ? (
      <>
      {msg.ok ? <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{msg.ok}</div> : null}
      {msg.err ? <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{msg.err}</div> : null}

      <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
        {/* SumUp settings */}
        <section className="min-w-0 rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_28px_80px_-36px_rgba(11,28,38,0.24)]">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#14213d]">
              <CreditCard className="h-5 w-5 text-[#c8a25a]" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[#14213d]">Pagos · SumUp</h2>
              <p className="text-sm text-slate-500">Cobro con tarjeta mediante checkout alojado.</p>
            </div>
          </div>

          {/* Status */}
          <div className="mt-5 flex flex-wrap gap-2">
            <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${
              settings?.enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'
            }`}>
              {settings?.enabled ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
              {settings?.enabled ? 'Pagos activos' : 'Pagos desactivados'}
            </span>
            <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${
              settings?.configured ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
            }`}>
              {settings?.configured ? 'Credenciales configuradas' : 'Faltan credenciales'}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600">
              Origen: {settings?.source || '—'}
            </span>
          </div>

          <div className="mt-6 space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Merchant code</label>
              <input className={`${inputCls} mt-1.5`} value={form.merchant_code} onChange={(e) => set('merchant_code', e.target.value)} placeholder="MCXXXXXX" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                API key {settings?.api_key_set ? <span className="ml-1 font-normal normal-case tracking-normal text-emerald-600">· guardada {settings.api_key_hint}</span> : null}
              </label>
              <input
                type="password"
                autoComplete="new-password"
                className={`${inputCls} mt-1.5`}
                value={form.api_key}
                onChange={(e) => set('api_key', e.target.value)}
                placeholder={settings?.api_key_set ? 'Déjalo vacío para mantener la actual' : 'sup_sk_...'}
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                Secreto del webhook {settings?.webhook_secret_set ? <span className="ml-1 font-normal normal-case tracking-normal text-emerald-600">· guardado</span> : null}
              </label>
              <input
                type="password"
                autoComplete="new-password"
                className={`${inputCls} mt-1.5`}
                value={form.webhook_secret}
                onChange={(e) => set('webhook_secret', e.target.value)}
                placeholder={settings?.webhook_secret_set ? 'Déjalo vacío para mantener el actual' : 'Opcional, verifica la firma'}
              />
              <p className="mt-1 text-xs text-slate-500">Si lo rellenas, se comprobará la firma HMAC de cada aviso de SumUp.</p>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">API base URL</label>
              <input className={`${inputCls} mt-1.5`} value={form.api_base_url} onChange={(e) => set('api_base_url', e.target.value)} />
            </div>

            <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              <input type="checkbox" checked={form.enabled} onChange={(e) => set('enabled', e.target.checked)} className="h-4 w-4" />
              <span className="text-sm font-semibold text-[#14213d]">Aceptar pagos online</span>
            </label>

            <button
              type="button"
              disabled={saveMutation.isPending}
              onClick={() => { setMsg({ ok: '', err: '' }); saveMutation.mutate(); }}
              className="w-full rounded-full bg-[#1fa5a3] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#188b89] disabled:opacity-60"
            >
              {saveMutation.isPending ? 'Guardando…' : 'Guardar ajustes'}
            </button>
          </div>
        </section>

        <aside className="min-w-0 space-y-6">
          <section className="min-w-0 rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_28px_80px_-36px_rgba(11,28,38,0.24)]">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-[#1fa5a3]" />
              <h2 className="text-lg font-bold text-[#14213d]">URLs para SumUp</h2>
            </div>
            <p className="mt-1 text-sm text-slate-500">Pega estas direcciones en el panel de desarrollador de SumUp.</p>
            <div className="mt-5 space-y-4">
              <CopyField label="Redirect URL" value={settings?.redirect_url} hint="A dónde vuelve el cliente tras pagar." />
              <CopyField label="Webhook URL" value={settings?.webhook_url} hint="Avisos de cambio de estado del pago." />
            </div>
          </section>

          <section className="min-w-0 rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_28px_80px_-36px_rgba(11,28,38,0.24)]">
            <div className="flex items-center gap-2">
              <Puzzle className="h-5 w-5 text-[#1fa5a3]" />
              <h2 className="text-lg font-bold text-[#14213d]">Módulos</h2>
            </div>
            <div className="mt-4 space-y-3">
              {plugins.map((p) => (
                <div key={p.id} className="rounded-2xl border border-slate-200 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-semibold text-[#14213d]">{p.name}</p>
                    <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${
                      p.enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {p.enabled ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">{p.category} · {p.description}</p>
                </div>
              ))}
              {!plugins.length ? <p className="text-sm text-slate-500">Sin módulos disponibles.</p> : null}
            </div>
          </section>
        </aside>
      </div>
      </>
      ) : null}

      {tab === 'email' ? (
      <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_28px_80px_-36px_rgba(11,28,38,0.24)]">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#14213d]">
            <Mail className="h-5 w-5 text-[#c8a25a]" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-[#14213d]">Correo · SMTP</h2>
            <p className="text-sm text-slate-500">Emails de confirmación al cliente y avisos de nueva reserva al equipo.</p>
          </div>
          <span className={`ml-auto inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${
            email?.configured ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
          }`}>
            {email?.configured ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
            {email?.configured ? 'Correo configurado' : 'Sin configurar'}
          </span>
        </div>

        {emailMsg.ok ? <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{emailMsg.ok}</div> : null}
        {emailMsg.err ? <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{emailMsg.err}</div> : null}

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Servidor SMTP (host)</label>
            <input className={`${inputCls} mt-1.5`} value={emailForm.host} onChange={(e) => setE('host', e.target.value)} placeholder="smtp.tuservidor.com" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Puerto</label>
              <input type="number" className={`${inputCls} mt-1.5`} value={emailForm.port} onChange={(e) => setE('port', Number(e.target.value))} placeholder="587" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Seguridad</label>
              <select className={`${inputCls} mt-1.5`} value={emailForm.security} onChange={(e) => setE('security', e.target.value)}>
                <option value="starttls">STARTTLS (587)</option>
                <option value="ssl">SSL (465)</option>
                <option value="none">Ninguna</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Usuario</label>
            <input className={`${inputCls} mt-1.5`} value={emailForm.username} onChange={(e) => setE('username', e.target.value)} placeholder="info@owawild.com" autoComplete="off" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
              Contraseña {email?.password_set ? <span className="ml-1 font-normal normal-case tracking-normal text-emerald-600">· guardada</span> : null}
            </label>
            <input type="password" autoComplete="new-password" className={`${inputCls} mt-1.5`} value={emailForm.password} onChange={(e) => setE('password', e.target.value)} placeholder={email?.password_set ? 'Déjalo vacío para mantener la actual' : '••••••••'} />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Remitente (email)</label>
            <input className={`${inputCls} mt-1.5`} value={emailForm.from_email} onChange={(e) => setE('from_email', e.target.value)} placeholder="info@owawild.com" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Remitente (nombre)</label>
            <input className={`${inputCls} mt-1.5`} value={emailForm.from_name} onChange={(e) => setE('from_name', e.target.value)} placeholder="OWA Wild Adventure" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Email de avisos (nueva reserva → equipo)</label>
            <input className={`${inputCls} mt-1.5`} value={emailForm.notify_email} onChange={(e) => setE('notify_email', e.target.value)} placeholder="info@owawild.com" />
            <p className="mt-1 text-xs text-slate-500">A esta dirección llega el aviso cuando entra una reserva pagada.</p>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          <button type="button" disabled={emailSave.isPending}
            onClick={() => { setEmailMsg({ ok: '', err: '' }); emailSave.mutate(); }}
            className="rounded-full bg-[#1fa5a3] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#188b89] disabled:opacity-60">
            {emailSave.isPending ? 'Guardando…' : 'Guardar correo'}
          </button>
          <button type="button" disabled={emailTest.isPending || !email?.configured}
            onClick={() => { setEmailMsg({ ok: '', err: '' }); emailTest.mutate(); }}
            className="rounded-full border border-slate-200 px-5 py-3 text-sm font-semibold text-[#14213d] transition hover:border-[#1fa5a3] hover:text-[#1fa5a3] disabled:opacity-50">
            {emailTest.isPending ? 'Enviando…' : 'Enviar email de prueba'}
          </button>
        </div>
      </section>

      ) : null}

      {tab === 'social' ? (
      <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_28px_80px_-36px_rgba(11,28,38,0.24)]">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#14213d]">
            <LogIn className="h-5 w-5 text-[#c8a25a]" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-[#14213d]">Acceso social · Google / Facebook</h2>
            <p className="text-sm text-slate-500">Deja que los clientes entren con un clic. Los botones solo aparecen si rellenas los IDs.</p>
          </div>
          <span className={`ml-auto inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${
            social?.google_client_id || social?.facebook_app_id ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'
          }`}>
            {social?.google_client_id ? 'Google ✓' : 'Google —'} · {social?.facebook_app_id ? 'Facebook ✓' : 'Facebook —'}
          </span>
        </div>

        {socialMsg.ok ? <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{socialMsg.ok}</div> : null}
        {socialMsg.err ? <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{socialMsg.err}</div> : null}

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Google · Client ID</label>
            <input className={`${inputCls} mt-1.5`} value={socialForm.google_client_id} onChange={(e) => setS('google_client_id', e.target.value)} placeholder="xxxx.apps.googleusercontent.com" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Facebook · App ID</label>
            <input className={`${inputCls} mt-1.5`} value={socialForm.facebook_app_id} onChange={(e) => setS('facebook_app_id', e.target.value)} placeholder="1234567890" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
              Facebook · App Secret {social?.facebook_secret_set ? <span className="ml-1 font-normal normal-case tracking-normal text-emerald-600">· guardado</span> : null}
            </label>
            <input type="password" autoComplete="new-password" className={`${inputCls} mt-1.5`} value={socialForm.facebook_app_secret} onChange={(e) => setS('facebook_app_secret', e.target.value)} placeholder={social?.facebook_secret_set ? 'Déjalo vacío para mantener el actual' : '••••••••'} />
          </div>
        </div>

        <button type="button" disabled={socialSave.isPending}
          onClick={() => { setSocialMsg({ ok: '', err: '' }); socialSave.mutate(); }}
          className="mt-5 rounded-full bg-[#1fa5a3] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#188b89] disabled:opacity-60">
          {socialSave.isPending ? 'Guardando…' : 'Guardar acceso social'}
        </button>
      </section>
      ) : null}

      {tab === 'social-feed' ? (
      <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_28px_80px_-36px_rgba(11,28,38,0.24)]">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#14213d]">
            <Instagram className="h-5 w-5 text-[#c8a25a]" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-[#14213d]">Redes sociales · Instagram / TikTok</h2>
            <p className="text-sm text-slate-500">Pega el enlace de cada publicación que quieras mostrar en la sección "Síguenos" del Home. Sin feed automático: tú eliges qué se ve.</p>
          </div>
        </div>

        {feedMsg.ok ? <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{feedMsg.ok}</div> : null}
        {feedMsg.err ? <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{feedMsg.err}</div> : null}

        <div className="mt-6 grid gap-4 sm:grid-cols-[140px_1fr]">
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Red</label>
            <select
              className={`${inputCls} mt-1.5`}
              value={feedForm.platform}
              onChange={(e) => setFeedForm((c) => ({ ...c, platform: e.target.value }))}
            >
              <option value="instagram">Instagram</option>
              <option value="tiktok">TikTok</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
              Enlace de la publicación
            </label>
            <input
              className={`${inputCls} mt-1.5`}
              value={feedForm.url}
              onChange={(e) => setFeedForm((c) => ({ ...c, url: e.target.value }))}
              placeholder={feedForm.platform === 'instagram' ? 'https://www.instagram.com/p/XXXXXXXXXXX/' : 'https://www.tiktok.com/@usuario/video/1234567890123456789'}
            />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Nota interna (opcional)</label>
            <input
              className={`${inputCls} mt-1.5`}
              value={feedForm.caption}
              onChange={(e) => setFeedForm((c) => ({ ...c, caption: e.target.value }))}
              placeholder="Solo para identificarlo aquí, no se muestra en la web"
            />
          </div>
        </div>

        <button
          type="button"
          disabled={feedAdd.isPending || !feedForm.url.trim()}
          onClick={() => { setFeedMsg({ ok: '', err: '' }); feedAdd.mutate(); }}
          className="mt-5 rounded-full bg-[#1fa5a3] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#188b89] disabled:opacity-60"
        >
          {feedAdd.isPending ? 'Añadiendo…' : 'Añadir publicación'}
        </button>

        <div className="mt-8 space-y-3">
          <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-slate-500">
            Publicaciones activas ({feedItems.length})
          </h3>
          {feedItems.length ? (
            feedItems.map((item) => (
              <div key={item.id} className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 p-4">
                <div className="min-w-0">
                  <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${
                    item.platform === 'instagram' ? 'bg-pink-50 text-pink-600' : 'bg-slate-100 text-slate-700'
                  }`}>
                    {item.platform}
                  </span>
                  <p className="mt-1.5 truncate text-sm text-[#14213d]">{item.caption || item.url}</p>
                  <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-xs text-slate-400 hover:text-[#1fa5a3] truncate block">{item.url}</a>
                </div>
                <button
                  type="button"
                  disabled={feedDelete.isPending}
                  onClick={() => feedDelete.mutate(item.id)}
                  className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition hover:border-red-300 hover:text-red-600 disabled:opacity-50"
                  aria-label="Eliminar"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))
          ) : (
            <p className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
              Sin publicaciones todavía. La sección "Síguenos" no aparece en el Home hasta que añadas al menos una.
            </p>
          )}
        </div>
      </section>
      ) : null}

      {tab === 'seo' ? (
      <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_28px_80px_-36px_rgba(11,28,38,0.24)]">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#14213d]">
            <Search className="h-5 w-5 text-[#c8a25a]" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-[#14213d]">SEO · Global</h2>
            <p className="text-sm text-slate-500">Valores por defecto, Google Analytics 4, verificación de Search Console y el sitemap.</p>
          </div>
        </div>

        {seoMsg.ok ? <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{seoMsg.ok}</div> : null}
        {seoMsg.err ? <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{seoMsg.err}</div> : null}

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
              Meta título por defecto <span className="ml-1 font-normal normal-case tracking-normal text-slate-400">({seoForm.default_meta_title.length}/70)</span>
            </label>
            <input
              className={`${inputCls} mt-1.5`}
              maxLength={70}
              value={seoForm.default_meta_title}
              onChange={(e) => setSeo('default_meta_title', e.target.value)}
              placeholder="OWA Wild Adventure | Actividades marinas en Canarias"
            />
            <p className="mt-1 text-xs text-slate-500">Se usa cuando una página no define su propio título.</p>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
              Meta descripción por defecto <span className="ml-1 font-normal normal-case tracking-normal text-slate-400">({seoForm.default_meta_description.length}/200)</span>
            </label>
            <textarea
              className={`${inputCls} mt-1.5 min-h-[80px] resize-y`}
              maxLength={200}
              value={seoForm.default_meta_description}
              onChange={(e) => setSeo('default_meta_description', e.target.value)}
              placeholder="Reserva experiencias marinas y escapadas de naturaleza en Canarias..."
            />
          </div>

          {/* Google-style search result preview */}
          <div className="sm:col-span-2 rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Vista previa en Google</p>
            <div className="mt-2 max-w-xl">
              <p className="text-xs text-slate-600">owawild.com</p>
              <p className="mt-0.5 truncate text-lg text-[#1a0dab]">{seoForm.default_meta_title || 'OWA Wild Adventure'}</p>
              <p className="mt-0.5 line-clamp-2 text-sm text-slate-600">{seoForm.default_meta_description || 'Sin meta descripción configurada.'}</p>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Google Analytics 4 · Measurement ID</label>
            <input
              className={`${inputCls} mt-1.5`}
              value={seoForm.ga4_measurement_id}
              onChange={(e) => setSeo('ga4_measurement_id', e.target.value.toUpperCase())}
              placeholder="G-XXXXXXXXXX"
            />
            <p className="mt-1 text-xs text-slate-500">Si lo dejas vacío, se usa el ID configurado en el servidor (si existe). Se aplica sin redesplegar la web.</p>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Google Search Console · Código de verificación</label>
            <input
              className={`${inputCls} mt-1.5`}
              value={seoForm.search_console_verification}
              onChange={(e) => setSeo('search_console_verification', e.target.value)}
              placeholder="Contenido del meta tag google-site-verification"
            />
          </div>

          <label className="sm:col-span-2 flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            <input type="checkbox" checked={seoForm.sitemap_enabled} onChange={(e) => setSeo('sitemap_enabled', e.target.checked)} className="h-4 w-4" />
            <span className="text-sm font-semibold text-[#14213d]">Generar sitemap.xml automáticamente</span>
          </label>
        </div>

        <button
          type="button"
          disabled={seoSave.isPending}
          onClick={() => { setSeoMsg({ ok: '', err: '' }); seoSave.mutate(); }}
          className="mt-5 rounded-full bg-[#1fa5a3] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#188b89] disabled:opacity-60"
        >
          {seoSave.isPending ? 'Guardando…' : 'Guardar SEO'}
        </button>
      </section>
      ) : null}
    </AdminSection>
  );
}
